import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule, DxLoadIndicatorModule, DxPopupModule } from 'devextreme-angular';
import { AgentResearchLauncherService } from './agent-research-launcher.service';

@Component({
  selector: 'ag-crm-agent-research-launcher',
  standalone: true,
  imports: [CommonModule, DxButtonModule, DxPopupModule, DxLoadIndicatorModule],
  templateUrl: './agent-research-launcher.component.html',
  styleUrls: ['./agent-research-launcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'ag-agent-research' },
  providers: [AgentResearchLauncherService],
})
export class AgentResearchLauncherComponent {
  @ViewChild('termInput') private termInput?: ElementRef<HTMLInputElement>;

  protected readonly researchService = inject(AgentResearchLauncherService);

  // Cmd/Ctrl+K opens the tool and focuses the search box; Escape closes it.
  //
  // Not gated on canUse: that signal resolves a beat after page load, so testing
  // it here made the shortcut dead on a fresh load. It does not need gating —
  // the template wraps the launcher AND the panel in *ngIf="canUse()", so this
  // only flips panelVisible and nothing renders without the permission. The
  // search endpoint enforces it too (403), so the gate here is about not
  // offering a tool the user may not use, not about protecting the data.
  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
    if (isShortcut) {
      event.preventDefault();
      this.openOrFocus();
      return;
    }
    if (event.key !== 'Escape') {
      return;
    }
    if (!this.researchService.panelVisible()) {
      return;
    }
    this.researchService.closePanel();
  }

  protected onPanelShown(): void {
    this.focusSearch();
  }

  protected onPanelHidden(): void {
    this.researchService.closePanel();
  }

  private openOrFocus(): void {
    if (this.researchService.panelVisible()) {
      this.focusSearch();
      return;
    }
    this.researchService.openPanel();
  }

  private focusSearch(): void {
    setTimeout(() => {
      const input = this.resolveSearchInput();
      if (input === null) {
        return;
      }
      input.focus();
      input.select();
    }, 60);
  }

  private resolveSearchInput(): HTMLInputElement | null {
    const fromViewChild = this.termInput?.nativeElement;
    if (fromViewChild) {
      return fromViewChild;
    }
    return document.querySelector<HTMLInputElement>('.ar-search input');
  }
}
